import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, ClipboardCheck, Loader2, Timer } from 'lucide-react';
import { fetchStartingEvaluation, submitStartingEvaluation } from '../api/candidate';
import type { StartingEvaluation, StartingEvalDefinition, StartingEvalQuestion, StartingEvalSection } from '../types';
import { Button, Card, EmptyState, ProgressBar, cx } from '../components/charte';

// Évaluation de départ (version du 17/10/2026) : un parcours écran par écran. Les rubriques de
// renseignements tiennent sur une page ; le quiz du véhicule pose une question par écran (« 1 / 6 ») ;
// les exercices de mémoire montrent des étapes pendant un chrono, puis la question les remplace sur le
// même écran, sans écran de passage (J6, 28/10/2026).

type Answers = Record<string, string | string[]>;

type Step =
  | { kind: 'page'; section: StartingEvalSection }
  | { kind: 'question'; section: StartingEvalSection; q: StartingEvalQuestion; index: number; total: number };

/** Écrans du parcours : une page par rubrique, ou une question par écran (son chrono à mémoriser compris). */
function buildSteps(definition: StartingEvalDefinition): Step[] {
  return definition.sections.flatMap((section): Step[] => {
    if (section.layout !== 'steps') return [{ kind: 'page', section }];
    const total = section.questions.length;
    return section.questions.map((q, i): Step => ({ kind: 'question', section, q, index: i + 1, total }));
  });
}

/**
 * Brouillon d'avant J6 (sans `memorized`) : l'écran à mémoriser comptait pour un écran. Écran retrouvé dans le
 * parcours d'aujourd'hui, et chronos déjà passés (question atteinte).
 */
function fromLegacyDraft(steps: Step[], legacyIndex: number): { step: number; memorized: string[] } {
  const memorized: string[] = [];
  let legacy = 0;
  for (const [i, s] of steps.entries()) {
    const memo = s.kind === 'question' && !!s.q.memorize;
    if (memo && legacyIndex >= legacy + 1) memorized.push(s.q.code);
    legacy += memo ? 2 : 1;
    if (legacyIndex < legacy) return { step: i, memorized };
  }
  return { step: steps.length - 1, memorized };
}

const LETTERS = 'ABCDEFGH';

/** Réponse donnée (obligatoire) : texte non vide, date, au moins un choix, ordre complet. */
function answered(q: StartingEvalQuestion, v: string | string[] | undefined): boolean {
  if (q.type === 'order') return Array.isArray(v) && v.length === (q.options?.length ?? 0) && v.every(Boolean);
  if (Array.isArray(v)) return v.length > 0;
  if (q.type === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');
  return !!v && v.trim() !== '';
}

const needsAnswer = (q: StartingEvalQuestion, answers: Answers) => !q.optional && !answered(q, answers[q.code]);

// Brouillon dans le navigateur : un rechargement ne fait pas tout recommencer. `memorized` : questions dont le chrono
// est passé (absent d'un brouillon d'avant J6).
type Draft = { answers: Answers; step: number; memorized?: string[] };
const draftKey = (data: StartingEvaluation) => `pmd-candidat-evaluation:${data.requestedAt ?? ''}:${data.definition.version}`;
function readDraft(key: string): Draft | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}
function writeDraft(key: string, value: Draft | null) {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch {
    /* stockage indisponible : le parcours continue sans brouillon */
  }
}

function Pill({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'min-h-[40px] rounded-pill border px-4 py-2 text-left text-sm font-medium transition',
        active ? 'border-primary bg-primary text-white shadow-btn' : 'border-line bg-white text-body hover:border-primary-200',
      )}
    >
      {label}
    </button>
  );
}

/** Choix A, B, C… en lignes pleine largeur (quiz et sélection multiple). */
function LetteredChoices({ q, selected, onPick }: { q: StartingEvalQuestion; selected: string[]; onPick: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-2.5" role="group" aria-label={q.label}>
      {q.options?.map((opt, i) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onPick(opt.value)}
            className={cx(
              'flex min-h-[52px] items-center gap-3 rounded-xl border px-4 py-3 text-left text-[15px] font-medium transition',
              active ? 'border-primary bg-surface-indigo text-primary-700 ring-2 ring-primary-100' : 'border-line bg-white text-body hover:border-primary-200',
            )}
          >
            <span
              aria-hidden
              className={cx(
                'grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold',
                active ? 'bg-primary text-white' : 'bg-surface-light text-secondary',
              )}
            >
              {LETTERS[i]}
            </span>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/** Étapes à numéroter : chaque numéro n'est donné qu'une fois. Réponse = étapes dans l'ordre des numéros. */
function OrderField({ q, value, onChange }: { q: StartingEvalQuestion; value: string[]; onChange: (v: string[]) => void }) {
  const count = q.options?.length ?? 0;
  const positions = Array.from({ length: count }, (_, i) => value[i] ?? '');
  const setPosition = (item: string, position: number) => {
    const next = positions.map((v) => (v === item ? '' : v));
    if (position > 0) next[position - 1] = item;
    onChange(next);
  };
  return (
    <ul className="flex flex-col gap-2.5">
      {q.options?.map((opt) => {
        const current = positions.indexOf(opt.value) + 1;
        return (
          <li key={opt.value} className="flex items-center gap-3 rounded-xl border border-line bg-white px-3 py-2.5">
            <select
              aria-label={`Numéro de l’étape : ${opt.label}`}
              value={current || ''}
              onChange={(e) => setPosition(opt.value, Number(e.target.value))}
              className="h-10 w-16 shrink-0 rounded-lg border border-line bg-surface-light px-2 text-center text-[15px] font-semibold text-heading focus:border-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-50"
            >
              <option value="">—</option>
              {Array.from({ length: count }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            <span className="text-[15px] text-body">{opt.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

const inputClass =
  'h-11 w-full rounded-xl border border-line bg-surface-light px-3.5 text-sm text-body outline-none focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50';

/**
 * Question d'une rubrique sur une page (renseignements, motivation…). `fromRecord` : réponse
 * proposée d'après la fiche de l'élève (date de naissance).
 */
function PageQuestion({ q, value, onChange, missing, fromRecord }: { q: StartingEvalQuestion; value: string | string[] | undefined; onChange: (v: string | string[]) => void; missing: boolean; fromRecord: boolean }) {
  const text = typeof value === 'string' ? value : '';
  return (
    <div className={cx('border-t border-slate-100 py-4 first:border-t-0 first:pt-0', missing && 'rounded-row bg-danger-light/60 px-3 first:pt-3')}>
      <label className="mb-2.5 block text-sm font-medium text-heading" htmlFor={`q-${q.code}`}>
        {q.label}
        {!q.optional && <span className="text-primary"> *</span>}
      </label>
      {q.type === 'text' ? (
        <input id={`q-${q.code}`} type="text" value={text} onChange={(e) => onChange(e.target.value)} className={inputClass} placeholder={q.placeholder ?? 'Votre réponse…'} />
      ) : q.type === 'date' ? (
        <input id={`q-${q.code}`} type="date" value={text} onChange={(e) => onChange(e.target.value)} className={cx(inputClass, 'max-w-[220px]')} />
      ) : (
        <div className="flex flex-wrap gap-2" role="group" aria-label={q.label}>
          {q.options?.map((opt) => (
            <Pill key={opt.value} active={text === opt.value} label={opt.label} onClick={() => onChange(opt.value)} />
          ))}
        </div>
      )}
      {q.hint && <p className="mt-2 text-xs text-muted">{q.hint}</p>}
      {fromRecord && <p className="mt-2 text-xs text-muted">Date reprise de votre fiche élève. Si elle est fausse, corrigez-la et prévenez votre auto-école.</p>}
      {missing && <p className="mt-2 text-xs font-medium text-danger-dark">Réponse attendue.</p>}
    </div>
  );
}

/** Étapes à mémoriser, dans l'ordre, pendant le chrono ; la question les remplace à la fin. */
function Memorize({ q, remaining }: { q: StartingEvalQuestion; remaining: number }) {
  const memo = q.memorize!;
  return (
    <div>
      <h3 className="text-lg font-semibold text-heading">{memo.title}</h3>
      {memo.text && <p className="mt-2 text-sm text-secondary">{memo.text}</p>}
      <ol className="mt-4 flex flex-col gap-2">
        {memo.steps.map((label, i) => (
          <li key={label} className="flex items-center gap-3 rounded-xl bg-surface-indigo px-4 py-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-white">{i + 1}</span>
            <span className="text-[15px] text-body">{label}</span>
          </li>
        ))}
      </ol>
      <p className="mt-5 flex items-center gap-2 rounded-row bg-warn-soft px-4 py-3 text-sm text-warn-fg" role="status">
        <Timer size={16} aria-hidden />
        Vous pourrez répondre à la question une fois le chrono fini.
        <strong className="ml-auto whitespace-nowrap tabular-nums">{remaining} s</strong>
      </p>
    </div>
  );
}

function Finished({ data, onOpenLivret }: { data: StartingEvaluation; onOpenLivret?: () => void }) {
  return (
    <Card className="text-center">
      <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-success-light text-success">
        <CheckCircle2 size={26} aria-hidden />
      </span>
      <h2 className="text-title-sm text-heading">Vous avez fini votre évaluation de départ 🎉✨</h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-secondary">Merci d’avoir répondu à toutes les questions.</p>
      {data.final ? (
        <p className="mx-auto mt-4 max-w-md rounded-row bg-surface-indigo px-4 py-3 text-sm text-primary-700">
          <strong>
            Votre enseignant propose {data.final.proposedTotalHours} h de formation, dont {data.final.proposedPracticeHours} h de conduite.
          </strong>
        </p>
      ) : (
        <>
          {data.estimatedHours != null && (
            <p className="mx-auto mt-4 max-w-md rounded-row bg-surface-indigo px-4 py-3 text-sm text-primary-700">
              {/* Une fourchette (J7) ; le contrat en garde le haut. Serveur plus ancien : le nombre seul. */}
              <strong>
                {data.estimatedRange
                  ? `D’après vos réponses, nous estimons votre formation de ${data.estimatedRange.from} à ${data.estimatedRange.to} h de conduite.`
                  : `D’après vos réponses, nous estimons votre formation à ${data.estimatedHours} h de conduite.`}
              </strong>{' '}
              Cette estimation est indicative : votre enseignant la confirmera après l’évaluation pratique.
            </p>
          )}
          <p className="mx-auto mt-3 max-w-md text-sm text-secondary">
            Le récapitulatif de votre évaluation va être envoyé à votre enseignant. Nous vous enverrons une notification par
            mail dès que celui-ci aura proposé une estimation.
          </p>
        </>
      )}
      {onOpenLivret && (
        <Button variant="primary" icon={BookOpen} className="mt-5" onClick={onOpenLivret}>
          Retourner au livret numérique
        </Button>
      )}
    </Card>
  );
}

function Title({ children }: { children?: React.ReactNode }) {
  return (
    <div>
      <h1 className="text-title-md text-heading sm:text-title-lg">Évaluation de départ</h1>
      {children}
    </div>
  );
}

function Wizard({ data }: { data: StartingEvaluation }) {
  const queryClient = useQueryClient();
  const steps = useMemo(() => buildSteps(data.definition), [data.definition]);
  const key = draftKey(data);
  const [draft] = useState(() => {
    const saved = readDraft(key);
    if (!saved || saved.memorized) return saved;
    return { ...saved, ...fromLegacyDraft(steps, saved.step ?? 0) };
  });
  // Ce que la fiche sait déjà (date de naissance) est proposé ; le brouillon de l'élève l'emporte.
  const [answers, setAnswers] = useState<Answers>({ ...data.prefill, ...draft?.answers });
  const [current, setCurrent] = useState(Math.min(draft?.step ?? 0, steps.length - 1));
  const [memorized, setMemorized] = useState<string[]>(draft?.memorized ?? []);
  const [showErrors, setShowErrors] = useState(false);
  const step = steps[current];

  useEffect(() => writeDraft(key, { answers, step: current, memorized }), [key, answers, current, memorized]);

  // Chrono de la question à mémoriser, relancé à chaque arrivée sur elle (rechargement compris) tant qu'il n'est pas
  // passé ; à zéro, la question remplace les étapes sur le même écran.
  const memoCode = step.kind === 'question' && step.q.memorize && !memorized.includes(step.q.code) ? step.q.code : null;
  const memoSeconds = memoCode && step.kind === 'question' ? (step.q.memorize?.seconds ?? 0) : 0;
  const [tick, setTick] = useState<{ code: string; left: number } | null>(null);
  const remaining = memoCode ? (tick?.code === memoCode ? tick.left : memoSeconds) : 0;
  useEffect(() => {
    if (!memoCode) return;
    let left = memoSeconds;
    const timer = window.setInterval(() => {
      left = Math.max(0, left - 1);
      setTick({ code: memoCode, left });
      if (left === 0) {
        window.clearInterval(timer);
        setMemorized((prev) => (prev.includes(memoCode) ? prev : [...prev, memoCode]));
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [memoCode, memoSeconds]);

  const mutation = useMutation({
    mutationFn: () => submitStartingEvaluation(answers),
    onSuccess: (fresh) => {
      writeDraft(key, null);
      queryClient.setQueryData(['candidate', 'starting-evaluation'], fresh);
    },
  });

  const setAnswer = (code: string, v: string | string[]) => setAnswers((prev) => ({ ...prev, [code]: v }));
  const missing = step.kind === 'page' ? step.section.questions.filter((q) => needsAnswer(q, answers)).map((q) => q.code) : [];
  const ready = step.kind === 'question' ? !memoCode && !needsAnswer(step.q, answers) : true;
  const last = current === steps.length - 1;
  // Pas de retour depuis une question à mémoriser, pendant son chrono ni après.
  const canGoBack = current > 0 && !(step.kind === 'question' && step.q.memorize);

  const next = () => {
    if (step.kind === 'page' && missing.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    if (last) mutation.mutate();
    else setCurrent(current + 1);
  };

  const raw = step.kind === 'page' ? undefined : answers[step.q.code];
  return (
    <div className="grid gap-5 sm:gap-6">
      <Title>
        <div className="mt-3">
          <ProgressBar value={(current / steps.length) * 100} label="Avancement de l’évaluation" />
        </div>
      </Title>
      <Card className="mx-auto w-full max-w-2xl">
        <div className="mb-4 flex items-baseline justify-between gap-3">
          <h2 className="text-title-sm text-heading">{step.section.title}</h2>
          {step.kind !== 'page' && (
            <span className="shrink-0 text-sm font-semibold text-secondary">
              {step.index} / {step.total}
            </span>
          )}
        </div>

        {step.kind === 'page' &&
          step.section.questions.map((q) => (
            <PageQuestion key={q.code} q={q} value={answers[q.code]} onChange={(v) => setAnswer(q.code, v)} missing={showErrors && missing.includes(q.code)} fromRecord={!!data.prefill?.[q.code]} />
          ))}

        {step.kind === 'question' && memoCode && <Memorize q={step.q} remaining={remaining} />}

        {step.kind === 'question' && !memoCode && (
          <div>
            <h3 className="text-lg font-semibold text-heading">{step.q.label}</h3>
            {step.q.hint && <p className="mt-1 text-sm text-secondary">{step.q.hint}</p>}
            <div className="mt-4">
              {step.q.type === 'order' ? (
                <OrderField q={step.q} value={Array.isArray(raw) ? raw : []} onChange={(v) => setAnswer(step.q.code, v)} />
              ) : step.q.type === 'multi' ? (
                <LetteredChoices
                  q={step.q}
                  selected={Array.isArray(raw) ? raw : []}
                  onPick={(v) => {
                    const list = Array.isArray(raw) ? raw : [];
                    setAnswer(step.q.code, list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
                  }}
                />
              ) : (
                <LetteredChoices q={step.q} selected={typeof raw === 'string' ? [raw] : []} onPick={(v) => setAnswer(step.q.code, v)} />
              )}
            </div>
          </div>
        )}

        {showErrors && missing.length > 0 && (
          <p role="alert" className="mt-3 text-xs font-medium text-danger-dark">
            Merci de répondre aux questions obligatoires ({missing.length} restante{missing.length > 1 ? 's' : ''}).
          </p>
        )}
        {mutation.isError && (
          <p role="alert" className="mt-3 rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            Une erreur est survenue lors de l'envoi. Merci de réessayer.
          </p>
        )}

        <div className="mt-6 flex items-center gap-3">
          {canGoBack && (
            <Button variant="ghost" icon={ArrowLeft} onClick={() => { setShowErrors(false); setCurrent(current - 1); }}>
              Précédent
            </Button>
          )}
          <Button
            variant="primary"
            className="ml-auto"
            iconEnd={last ? undefined : ArrowRight}
            icon={last ? CheckCircle2 : undefined}
            disabled={!ready}
            loading={mutation.isPending}
            onClick={next}
          >
            {last ? 'Terminer' : 'Suivant'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default function EvaluationView({ onOpenLivret }: { onOpenLivret?: () => void }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'starting-evaluation'],
    queryFn: fetchStartingEvaluation,
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card className="flex items-center text-sm text-secondary">
        <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
      </Card>
    );
  }

  if (isError || !data || data.status === 'not_requested') {
    return (
      <div className="grid gap-5 sm:gap-6">
        <Title />
        <Card>
          <EmptyState icon={ClipboardCheck} title="Rien à remplir" description="Aucune évaluation de départ ne vous est demandée pour le moment." />
        </Card>
      </div>
    );
  }

  if (data.status === 'completed') {
    return (
      <div className="grid gap-5 sm:gap-6">
        <Title />
        <Finished data={data} onOpenLivret={onOpenLivret} />
      </div>
    );
  }

  return <Wizard data={data} />;
}
